import { piiInterceptor } from '@ringcx/pii-interceptor';

import { BaseTransport, LogParams } from './base';
import { LOG_LEVELS, LogLevel } from './constants';
import { IDBConfig, IDBUtil } from '../../idbUtil';
import { downloadFile, serializeMsgPartsMemoized } from '../util';

const DATE_INDEX = 'dateIndex';
const EXPIRE_DAYS = 7;
interface IMessage {
    messageParts: any[];
    level: LogLevel;
    dts: Date;
}

type UserConfigType = {
    accountId?: string;
    agentId?: string;
    agentType?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    loginType?: string;
    username?: string;
    platform?: string;
    version?: string;
    environment?: string;
    userAgent?: string;
};

export class IndexedDBTransport extends BaseTransport {
    static idbUtil: IDBUtil<IMessage>;
    private _userConfig: UserConfigType;

    constructor() {
        super();
        IndexedDBTransport.idbUtil = new IDBUtil(this._getDbConfig());
        this._purgeLogs();
        this._userConfig = {
            platform: process.env.PLATFORM,
            version: process.env.VERSION,
            environment: process.env.ENVIRONMENT,
            userAgent: navigator.userAgent,
        };
    }

    public initUserConfig(agentSettings: any) {
        this._userConfig.accountId = agentSettings?.accountId;
        this._userConfig.agentId = agentSettings?.agentId;
        this._userConfig.agentType = agentSettings?.agentType;
        this._userConfig.email = agentSettings?.email;
        this._userConfig.firstName = agentSettings?.firstName;
        this._userConfig.lastName = agentSettings?.lastName;
        this._userConfig.loginType = agentSettings?.loginType;
        this._userConfig.username = agentSettings?.username;
    }

    private _getDbConfig(): IDBConfig {
        return {
            version: 2,
            dbName: 'rcx_log',
            storeNames: LOG_LEVELS,
            storeConfigs: Array.from({ length: 6 }, () => ({
                options: { autoIncrement: true },
                indexConfigs: [{ indexName: DATE_INDEX, keyPath: 'dts' }],
            })),
        };
    }

    public log({ level, piiMaskRules, messageParts }: LogParams) {
        const sanitizedMessages = piiInterceptor.sanitize(messageParts, piiMaskRules);
        const message = { messageParts: serializeMsgPartsMemoized(sanitizedMessages), level, dts: new Date() };
        IndexedDBTransport.idbUtil.add(level, undefined, message);
    }
    private _purgeLogs() {
        LOG_LEVELS.forEach((level) => {
            this.purgeLogRecordsByLevel(level);
        });
    }
    async purgeLogRecordsByLevel(level: LogLevel) {
        const endDate = new Date();
        // Purge records older than 7 days
        endDate.setDate(endDate.getDate() - EXPIRE_DAYS);
        try {
            await IndexedDBTransport.idbUtil.removeByIndexUpper(level, DATE_INDEX, endDate);
        } catch (e) {
            console.error(e);
        }
    }

    private async _sortRecordsByTime(allLogs: IMessage[][]) {
        const flattenedLogs = allLogs.flat();
        const sortedLogs = flattenedLogs.sort((a: IMessage, b: IMessage) => {
            if (a.dts < b.dts) {
                return -1;
            }
            if (a.dts > b.dts) {
                return 1;
            }
            return 0;
        });
        return sortedLogs;
    }

    private async _extractLogWithTime(startDate?: Date, endDate?: Date, limit?: number) {
        let range: IDBKeyRange;
        if (startDate && endDate) {
            range = IDBKeyRange.bound(startDate, endDate);
        } else if (startDate) {
            range = IDBKeyRange.lowerBound(startDate);
        } else if (endDate) {
            range = IDBKeyRange.upperBound(endDate);
        } else {
            range = IDBKeyRange.upperBound(new Date());
        }
        const all = LOG_LEVELS.map((level) =>
            IndexedDBTransport.idbUtil.getLogsByBound(level, DATE_INDEX, range, limit)
        );
        return this._sortRecordsByTime(await Promise.all(all));
    }

    async getAllLogRecords() {
        const all = LOG_LEVELS.map((level) => IndexedDBTransport.idbUtil.getAllList(level));
        return this._sortRecordsByTime(await Promise.all(all));
    }

    getLogRecordsByTime(time: number) {
        const all = LOG_LEVELS.map((level) => IndexedDBTransport.idbUtil.getLogRecordsByTime(level, time, DATE_INDEX));
        return Promise.all(all).then((results) => results.flat());
    }

    async downloadLogs() {
        const allLogs = await this.getAllLogRecords();
        this._spliceDownloadLogs(allLogs);
    }

    async downloadLogsByRange(startDate?: Date, endDate?: Date, limit?: number) {
        const allLogs = await this._extractLogWithTime(startDate, endDate, limit);
        this._spliceDownloadLogs(allLogs);
    }

    private _spliceDownloadLogs(allLogs: IMessage[]) {
        const combinedData = {
            userConfig: piiInterceptor.sanitize(this._userConfig),
            logs: allLogs,
        };
        const blob = new Blob([JSON.stringify(combinedData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        downloadFile(url, `rcx_logs_${new Date().toISOString()}.json`);
        URL.revokeObjectURL(url);
    }
}
